import { useState, useMemo, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, Home, Factory, Hammer, 
  PencilRuler, HardHat, 
  ArrowRight, Settings, Building, Palette, Star, X 
} from 'lucide-react';
import PropTypes from 'prop-types';

// Construction company services with added interior design
const services = [
  {
    title: 'Residential Construction',
    description: 'Custom homes and residential developments built to perfection',
    details: 'Our residential construction services cover everything from luxury custom homes to multi-family developments. We prioritize quality craftsmanship, energy efficiency, and timely delivery, ensuring your dream home becomes reality. Our team handles all aspects from foundation to finishing touches.',
    image: '/assets/image1.jpg',
    icon: Home,
    color: 'bg-amber-600',
    category: 'residential',
    features: [
      'Custom Home Design',
      'Interior Finishing',
      'Smart Home Integration',
      'Energy Efficient Solutions'
    ],
    highlights: [
      { title: 'Projects Completed', value: '25+' },
      { title: 'Happy Families', value: '20+' },
      { title: 'Years Experience', value: '5+' }
    ]
  },
  {
    title: 'Commercial Construction',
    description: 'Modern commercial spaces for businesses of all sizes',
    details: 'We specialize in creating state-of-the-art commercial buildings including office spaces, retail centers, and mixed-use developments. Our expertise ensures your commercial project meets all building codes while maximizing functionality and aesthetic appeal.',
    image: '/assets/image1.jpg',
    icon: Building2,
    color: 'bg-blue-700',
    category: 'commercial',
    features: [
      'Office Space Design',
      'Retail Center Development',
      'Mixed-Use Development',
      'Commercial Building Design'
    ],
    highlights: [
      { title: 'Projects Completed', value: '8+' },
      { title: 'Happy Businesses', value: '6+' },
      { title: 'Years Experience', value: '5+' }
    ]
  },
  {
    title: 'Industrial Construction',
    description: 'Large-scale industrial facilities and warehouses',
    details: 'Our industrial construction services cater to manufacturing plants, warehouses, and processing facilities. We understand the unique requirements of industrial buildings and ensure theyre built to handle specific operational needs while maintaining safety standards.',
    image: '/assets/image1.jpg',
    icon: Factory,
    color: 'bg-slate-700',
    category: 'industrial',
    features: [
      'Heavy Machinery Handling',
      'Warehouse Construction',
      'Processing Facility Design',
      'Industrial Building Design'
    ],
    highlights: [
      { title: 'Projects Completed', value: '12+' },
      { title: 'Happy Industries', value: '8+' },
      { title: 'Years Experience', value: '5+' }
    ]
  },
  {
    title: 'Interior Design & Execution',
    description: 'Transform spaces with custom interior solutions',
    details: 'Our interior design services combine aesthetics with functionality to create stunning spaces that reflect your vision. From concept to execution, we handle everything including space planning, material selection, custom furnishings, and final styling to deliver exceptional interiors for both residential and commercial projects.',
    image: '/assets/image1.jpg',
    icon: Palette,
    color: 'bg-pink-600',
    category: 'interior',
    features: [
      'Space Planning',
      'Material Selection',
      'Custom Furnishings',
      'Final Styling'
    ],
    highlights: [
      { title: 'Projects Completed', value: '18+' },
      { title: 'Happy Clients', value: '15+' },
      { title: 'Years Experience', value: '5+' }
    ]
  },
  {
    title: 'Renovation Services',
    description: 'Modernize and upgrade existing structures',
    details: 'From minor upgrades to complete building overhauls, our renovation services breathe new life into existing structures. We specialize in both residential and commercial renovations, ensuring minimal disruption while maximizing property value.',
    image: '/assets/image1.jpg',
    icon: Hammer,
    color: 'bg-orange-600',
    category: 'renovation',
    features: [
      'Minor Upgrades',
      'Complete Building Overhauls',
      'Residential Renovations',
      'Commercial Renovations'
    ],
    highlights: [
      { title: 'Projects Completed', value: '15+' },
      { title: 'Happy Property Owners', value: '12+' },
      { title: 'Years Experience', value: '5+' }
    ]
  },
  {
    title: 'Architectural Design',
    description: 'Custom architectural solutions and planning',
    details: 'Our in-house architectural team creates innovative designs that blend functionality with aesthetics. We work closely with clients to develop plans that match their vision while adhering to local building codes and regulations.',
    image: '/assets/image1.jpg',
    icon: PencilRuler,
    color: 'bg-indigo-600',
    category: 'design',
    features: [
      'Custom Architectural Designs',
      'Planning and Execution',
      'Local Building Codes Adherence',
      'Aesthetic Appeal'
    ],
    highlights: [
      { title: 'Projects Completed', value: '22+' },
      { title: 'Happy Clients', value: '18+' },
      { title: 'Years Experience', value: '5+' }
    ]
  },
  {
    title: 'Construction Management',
    description: 'Expert project management and supervision',
    details: 'Our construction management services ensure your project runs smoothly from start to finish. We handle scheduling, coordination with subcontractors, quality control, and budget management to deliver projects on time and within budget.',
    image: '/assets/image1.jpg',
    icon: HardHat,
    color: 'bg-yellow-600',
    category: 'management',
    features: [
      'Project Management',
      'Subcontractor Coordination',
      'Quality Control',
      'Budget Management'
    ],
    highlights: [
      { title: 'Projects Completed', value: '28+' },
      { title: 'Happy Clients', value: '25+' },
      { title: 'Years Experience', value: '5+' }
    ]
  },
  {
    title: 'Infrastructure Development',
    description: 'Roads, bridges, and civil engineering projects',
    details: 'We undertake major infrastructure projects including road construction, bridge building, and utility installations. Our experienced team handles complex civil engineering challenges while maintaining the highest safety and quality standards.',
    image: '/assets/image1.jpg',
    icon: Building,
    color: 'bg-green-700',
    category: 'infrastructure',
    features: [
      'Road Construction',
      'Bridge Building',
      'Utility Installations',
      'Civil Engineering Projects'
    ],
    highlights: [
      { title: 'Projects Completed', value: '10+' },
      { title: 'Happy Communities', value: '8+' },
      { title: 'Years Experience', value: '5+' }
    ]
  },
  {
    title: 'Consulting & Planning',
    description: 'Expert construction consulting services',
    details: 'Our consulting services help clients make informed decisions about their construction projects. We provide feasibility studies, cost estimation, sustainability planning, and regulatory compliance guidance.',
    image: '/assets/image1.jpg',
    icon: Settings,
    color: 'bg-purple-600',
    category: 'consulting',
    features: [
      'Feasibility Studies',
      'Cost Estimation',
      'Sustainability Planning',
      'Regulatory Compliance Guidance'
    ],
    highlights: [
      { title: 'Projects Completed', value: '20+' },
      { title: 'Happy Clients', value: '16+' },
      { title: 'Years Experience', value: '5+' }
    ]
  }
];


// Add this before the Services component
const serviceCategories = [
  { id: 'all', name: 'All Services', icon: Building2 },
  { id: 'residential', name: 'Residential', icon: Home },
  { id: 'commercial', name: 'Commercial', icon: Building },
  { id: 'industrial', name: 'Industrial', icon: Factory },
  { id: 'interior', name: 'Interior', icon: Palette },
  { id: 'infrastructure', name: 'Infrastructure', icon: Building2 },
  { id: 'consulting', name: 'Consulting', icon: Settings }
];

// Service Card Component with PropTypes
const ServiceCard = ({ service, onClick }) => {
  return (
    <motion.div
      layout
      onClick={onClick}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -8, scale: 1.02 }}
      className="group relative bg-gradient-to-br from-white to-blue-50/30 rounded-2xl overflow-hidden shadow-lg 
        hover:shadow-2xl transition-all duration-500 cursor-pointer w-full max-w-sm
        flex flex-col h-full border border-blue-100 hover:border-cyan-200 hover:shadow-cyan-500/20"
    >
      {/* Service Image with Overlay */}
      <div className="relative h-48 sm:h-56 md:h-64 overflow-hidden">
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover transform group-hover:scale-110 
            transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 
          to-transparent group-hover:via-black/60 transition-all duration-500" 
        />
        
        {/* Category Badge */}
        <div className="absolute top-4 right-4">
          <div className="px-3 py-1.5 rounded-full text-sm font-medium 
            bg-white/10 backdrop-blur-md text-white border border-white/20
            group-hover:bg-white/20 transition-all duration-300"
          >
            {service.category}
          </div>
        </div>

        {/* Service Icon */}
        <div className={`absolute top-4 left-4 w-12 h-12 rounded-xl 
          bg-gradient-to-br from-cyan-600 via-blue-600 to-violet-600 p-2.5
          shadow-lg shadow-cyan-500/30 transform group-hover:scale-110 transition-all duration-300`}
        >
          <service.icon className="w-full h-full text-white" />
        </div>

        {/* Title and Description */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 
            transform group-hover:translate-x-2 transition-transform duration-300"
          >
            {service.title}
          </h3>
          <p className="text-white/80 text-sm line-clamp-2 transform 
            group-hover:translate-x-2 transition-transform duration-300 delay-75"
          >
            {service.description}
          </p>
        </div>
      </div>

      {/* Features Preview */}
      <div className="p-6 flex flex-col flex-grow">
        <div className="flex flex-wrap gap-2 mb-6">
          {service.features?.slice(0, 3).map((feature, idx) => (
            <span
              key={idx}
              className="px-3 py-1.5 bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-700 rounded-full 
                text-sm font-medium transform hover:scale-105 transition-all 
                duration-300 hover:shadow-md border border-cyan-200 hover:border-cyan-300"
            >
              {feature}
            </span>
          ))}
        </div>

        {/* Stats/Highlights */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {service.highlights?.slice(0, 2).map((highlight, idx) => (
            <div key={idx} 
              className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-3 text-center transform 
                hover:scale-105 transition-all duration-300 border border-cyan-100 hover:border-cyan-200"
            >
              <p className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                {highlight.value}
              </p>
              <p className="text-sm text-slate-600 mt-1">
                {highlight.title}
              </p>
            </div>
          ))}
        </div>

        {/* Learn More Button */}
        <button
          className="w-full mt-auto py-3 bg-gradient-to-r from-cyan-600 via-blue-600 to-violet-600 
            text-white rounded-xl text-sm font-medium group/btn flex items-center 
            justify-center gap-2 hover:shadow-lg hover:shadow-cyan-500/30 transform hover:-translate-y-0.5 
            transition-all duration-300"
        >
          <span>Learn More</span>
          <ArrowRight className="w-4 h-4 transform group-hover/btn:translate-x-1 
            transition-transform duration-300" 
          />
        </button>
      </div>
    </motion.div>
  );
};

ServiceCard.propTypes = {
  service: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    color: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    details: PropTypes.string.isRequired,
    features: PropTypes.arrayOf(PropTypes.string),
    highlights: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      })
    )
  }).isRequired,
  onClick: PropTypes.func.isRequired
};

const ServiceModal = ({ service, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
      onClick={(e) => e.stopPropagation()}
      className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl 
        overflow-hidden my-4 mx-auto max-h-[90vh] flex flex-col"
    >
      {/* Modal Header */}
      <div className="relative h-48 sm:h-56 md:h-64 flex-shrink-0">
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 
          via-black/60 to-transparent" 
        />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-2 rounded-full bg-black/20 
            hover:bg-black/40 text-white transition-colors z-10"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Service Info */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br 
              from-blue-600 to-blue-700 p-3 shadow-lg"
            >
              <service.icon className="w-full h-full text-white" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-white">
                {service.title}
              </h3>
              <p className="text-blue-200 text-sm mt-0.5">
                {service.category}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="overflow-y-auto flex-grow">
        {/* Modal Content */}
        <div className="p-6">
          {/* Service Description */}
          <p className="text-gray-700 text-base leading-relaxed mb-6">
            {service.details}
          </p>

          {/* Highlights Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            {service.highlights?.map((highlight, idx) => (
              <div key={idx} 
                className="bg-blue-50 rounded-xl p-3 text-center transform 
                  hover:scale-105 transition-all duration-300"
              >
                <p className="text-xl sm:text-2xl font-bold text-blue-600">
                  {highlight.value}
                </p>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  {highlight.title}
                </p>
              </div>
            ))}
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {service.features?.map((feature, idx) => (
              <div key={idx} 
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl 
                  border border-gray-100 hover:shadow-md transition-all duration-300"
              >
                <div className="p-1.5 rounded-lg bg-blue-100/50">
                  <Star className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-gray-700 text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-6 bg-gray-50 border-t border-gray-100">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 
              text-white rounded-xl font-medium hover:shadow-lg transform 
              hover:-translate-y-0.5 transition-all duration-300"
          >
            Close
          </button>
        </div>
      </div>
    </motion.div>
  );
};

ServiceModal.propTypes = {
  service: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    category: PropTypes.string.isRequired,
    details: PropTypes.string.isRequired,
    features: PropTypes.arrayOf(PropTypes.string),
    highlights: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      })
    )
  }).isRequired,
  onClose: PropTypes.func.isRequired
};

// Optimize the service categories navigation
const ServiceCategoryButton = memo(function ServiceCategoryButton({ category, isActive, onClick, isMobile }) {
  return (
  <motion.button
    onClick={() => onClick(category.id)}
    whileHover={{ scale: isMobile ? 1 : 1.02 }}
    whileTap={{ scale: 0.98 }}
    className={`
      ${isMobile 
        ? 'flex-shrink-0 px-4 py-2.5 text-sm whitespace-nowrap snap-start min-w-fit'
        : 'px-6 py-3 min-w-fit'
      }
      rounded-xl flex items-center justify-center gap-2 transition-all duration-300 font-medium
      ${isActive
        ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg shadow-cyan-500/30'
        : 'bg-white text-slate-600 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-blue-50 shadow hover:shadow-cyan-500/20 border border-slate-200 hover:border-cyan-200'
      }
    `}
  >
    <category.icon className={`${isMobile ? 'w-4 h-4 flex-shrink-0' : 'w-5 h-5 flex-shrink-0'}`} />
    <span className="text-center">{category.name}</span>
  </motion.button>
  );
});

ServiceCategoryButton.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired
  }).isRequired,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired
};

// Update the Services component with better organization
const Services = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredServices = useMemo(() => 
    activeCategory === 'all' 
      ? services 
      : services.filter(service => service.category === activeCategory),
    [activeCategory]
  );

  return (
    <section id="services" className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 
              bg-gradient-to-r from-slate-800 via-blue-800 to-cyan-800 bg-clip-text 
              text-transparent"
          >
            Our Services
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-600"
          >
            Comprehensive construction solutions tailored to your needs
          </motion.p>
        </div>

        {/* Categories Navigation */}
        <div className="mb-12">
          {/* Desktop Navigation */}
          <div className="hidden md:flex flex-wrap justify-center items-center gap-3 max-w-5xl mx-auto">
            {serviceCategories.map((category) => (
              <ServiceCategoryButton
                key={category.id}
                category={category}
                isActive={activeCategory === category.id}
                onClick={setActiveCategory}
                isMobile={false}
              />
            ))}
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden max-w-full">
            <div className="flex overflow-x-auto gap-3 pb-4 scrollbar-hide 
              snap-x snap-mandatory px-4 -mx-4"
            >
              {serviceCategories.map((category) => (
                <ServiceCategoryButton
                  key={category.id}
                  category={category}
                  isActive={activeCategory === category.id}
                  onClick={setActiveCategory}
                  isMobile={true}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <motion.div 
          layout
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 sm:gap-8 justify-items-center items-start"
        >
          <AnimatePresence mode="popLayout">
            {filteredServices.map((service) => (
              <ServiceCard
                key={service.title}
                service={service}
                onClick={() => setSelectedService(service)}
              />
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Empty State */}
        {filteredServices.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <p className="text-gray-500 text-lg">
              No services found in this category.
            </p>
          </motion.div>
        )}

        {/* Modal */}
        <AnimatePresence>
          {selectedService && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedService(null)}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 
                overflow-y-auto scrollbar-hide"
            >
              <div className="min-h-screen px-4 py-8 flex items-center justify-center">
                <ServiceModal 
                  service={selectedService} 
                  onClose={() => setSelectedService(null)} 
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Services;